import React from 'react';
import {
  DiscountConfig,
  ExchangeFormData,
  ExchangeStep,
  ExchangeSummary,
  ExchangeableItem,
  MarkupConfig,
  SalesOrder,
  SelectedProduct,
} from '../../../../types/sales.types';
import { WarehouseInventoryProduct } from '../../../../types/sales.types';
import { ExchangeStep2FlowPhase } from './ExchangeStep2Payment.hooks';
import ExchangeStep1Selection from './ExchangeStep1Selection';
import ExchangeStep2Payment from './ExchangeStep2Payment';
import ExchangeStep3Confirmation from './ExchangeStep3Confirmation';
import * as S from './ExchangeDrawer.styles';
import type { CommercialDateMode } from '../../../../utils/sales/commercial-sale-date';

export interface ExchangeFlowContentProps {
  currentStep: ExchangeStep;
  order: SalesOrder;
  eligibility: ExchangeableItem[];
  exchangeData: ExchangeFormData;
  exchangeSummary: ExchangeSummary;
  formatCurrency: (amount: number) => string;
  paymentFlowPhase: ExchangeStep2FlowPhase;
  setPaymentFlowPhase: (phase: ExchangeStep2FlowPhase) => void;
  paymentsLocked: boolean;
  setPaymentsLocked: (locked: boolean) => void;
  onSelectItemsToExchange: (itemIds: number[]) => void;
  onAddNewProduct: (product: WarehouseInventoryProduct) => void;
  onRemoveNewProduct: (productId: number) => void;
  onClearNewProducts: () => void;
  onUpdateNewProduct: (productId: number, updates: Partial<SelectedProduct>) => void;
  updateNewProductMarkup: (
    productId: number,
    markupType: 'PERCENTAGE' | 'AMOUNT' | 'NONE',
    markupValue: number,
  ) => void;
  updateNewProductDiscount: (
    productId: number,
    discountType: 'PERCENTAGE' | 'AMOUNT' | 'NONE',
    discountValue: number,
  ) => void;
  onUpdateExchangeNotes: (notes: string) => void;
  onClearPayments: () => void;
  onAddPayment: (
    method: string,
    amount: number,
    cashReceived?: number,
    notes?: string,
    bankReference?: string,
    qrTransactionId?: string,
  ) => void;
  onRemovePayment: (paymentId: string) => void;
  onUpdatePayment: (
    paymentId: string,
    amount: number,
    cashReceived?: number,
    notes?: string,
    bankReference?: string,
    qrTransactionId?: string,
  ) => void;
  resetAllLinePricing: () => void;
  commitCommercialPricingConfig: (draft: {
    markupEnabled: boolean;
    allowIndividualMarkups: boolean;
    markupType: 'PERCENTAGE' | 'AMOUNT';
    markupValue: number;
    discountEnabled: boolean;
    allowIndividualDiscounts: boolean;
    discountType: 'PERCENTAGE' | 'AMOUNT';
    discountValue: number;
  }) => void;
  updateMarkupConfig: (config: Partial<MarkupConfig>) => void;
  updateDiscountConfig: (config: Partial<DiscountConfig>) => void;
  applyUniformMarkupToNewProducts: (
    markupType: 'PERCENTAGE' | 'AMOUNT',
    markupValue: number,
  ) => void;
  applyUniformDiscountToNewProducts: (
    discountType: 'PERCENTAGE' | 'AMOUNT',
    discountValue: number,
  ) => void;
  onPartialPaymentToggle: (enabled: boolean) => void;
  onMultiplePaymentsToggle: (enabled: boolean) => void;
  getProductFinalPrice: (product: SelectedProduct) => number;
  onToggleDelivered: (productId: number, isDelivered: boolean) => void;
  canModifySaleDate: boolean;
  exchangeDateMode: CommercialDateMode | null;
  exchangeDate: Date;
  exchangeDateModeError: boolean;
  onExchangeDateModeChange: (mode: CommercialDateMode) => void;
  onExchangeDateChange: (date: Date) => void;
}

const ExchangeFlowContent: React.FC<ExchangeFlowContentProps> = ({
  currentStep,
  order,
  eligibility,
  exchangeData,
  exchangeSummary,
  formatCurrency,
  paymentFlowPhase,
  setPaymentFlowPhase,
  paymentsLocked,
  setPaymentsLocked,
  onSelectItemsToExchange,
  onAddNewProduct,
  onRemoveNewProduct,
  onClearNewProducts,
  onUpdateNewProduct,
  updateNewProductMarkup,
  updateNewProductDiscount,
  onUpdateExchangeNotes,
  onClearPayments,
  onAddPayment,
  onRemovePayment,
  onUpdatePayment,
  resetAllLinePricing,
  commitCommercialPricingConfig,
  updateMarkupConfig,
  updateDiscountConfig,
  applyUniformMarkupToNewProducts,
  applyUniformDiscountToNewProducts,
  onPartialPaymentToggle,
  onMultiplePaymentsToggle,
  getProductFinalPrice,
  onToggleDelivered,
  canModifySaleDate,
  exchangeDateMode,
  exchangeDate,
  exchangeDateModeError,
  onExchangeDateModeChange,
  onExchangeDateChange,
}) => {
  switch (currentStep) {
    case 'SELECTION':
      return (
        <S.StepScrollArea>
          <ExchangeStep1Selection
            order={order}
            eligibility={eligibility}
            exchangeData={exchangeData}
            onSelectItemsToExchange={onSelectItemsToExchange}
            onAddNewProduct={onAddNewProduct}
            onRemoveNewProduct={onRemoveNewProduct}
            onClearNewProducts={onClearNewProducts}
            onUpdateNewProduct={onUpdateNewProduct}
            onUpdateExchangeNotes={onUpdateExchangeNotes}
            exchangeSummary={exchangeSummary}
            formatCurrency={formatCurrency}
          />
        </S.StepScrollArea>
      );
    case 'PAYMENT':
      return (
        <S.StepScrollArea>
          <ExchangeStep2Payment
            order={order}
            exchangeSummary={exchangeSummary}
            exchangeData={exchangeData}
            paymentFlowPhase={paymentFlowPhase}
            setPaymentFlowPhase={setPaymentFlowPhase}
            paymentsLocked={paymentsLocked}
            setPaymentsLocked={setPaymentsLocked}
            onClearPayments={onClearPayments}
            onAddPayment={onAddPayment}
            onRemovePayment={onRemovePayment}
            onUpdatePayment={onUpdatePayment}
            updateNewProductMarkup={updateNewProductMarkup}
            updateNewProductDiscount={updateNewProductDiscount}
            resetAllLinePricing={resetAllLinePricing}
            commitCommercialPricingConfig={commitCommercialPricingConfig}
            updateMarkupConfig={updateMarkupConfig}
            updateDiscountConfig={updateDiscountConfig}
            applyUniformMarkupToNewProducts={applyUniformMarkupToNewProducts}
            applyUniformDiscountToNewProducts={applyUniformDiscountToNewProducts}
            onPartialPaymentToggle={onPartialPaymentToggle}
            onMultiplePaymentsToggle={onMultiplePaymentsToggle}
            getProductFinalPrice={getProductFinalPrice}
            formatCurrency={formatCurrency}
          />
        </S.StepScrollArea>
      );
    case 'CONFIRMATION':
      return (
        <ExchangeStep3Confirmation
          order={order}
          exchangeData={exchangeData}
          exchangeSummary={exchangeSummary}
          formatCurrency={formatCurrency}
          getProductFinalPrice={getProductFinalPrice}
          onToggleDelivered={onToggleDelivered}
          canModifySaleDate={canModifySaleDate}
          exchangeDateMode={exchangeDateMode}
          exchangeDate={exchangeDate}
          exchangeDateModeError={exchangeDateModeError}
          onExchangeDateModeChange={onExchangeDateModeChange}
          onExchangeDateChange={onExchangeDateChange}
        />
      );
    default:
      return null;
  }
};

export default ExchangeFlowContent;
